import * as React from 'react';
import * as LabelPrimitive from '@radix-ui/react-label';
import { cn } from '@relay-towing/tokens';
import {
  fieldDescriptionRecipe,
  fieldLabelRecipe,
  fieldMessageRecipe,
  fieldRecipe,
  fieldRequiredMarkerRecipe,
  type FieldLabelRecipeProps,
  formActionsRecipe,
  formLayoutRecipe,
  type FieldRecipeProps,
  readOnlyValueRecipe,
  type ReadOnlyValueRecipeProps,
} from '../recipes/field.recipe';
import { FieldContext, type FieldContextValue } from './field-context';
import { Text } from './Text';
import { uiMarkerInternal } from '../recipes/shared';

export interface FieldProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, 'children'>,
    FieldRecipeProps {
  /** Visible label. Every field should have one. */
  label?: React.ReactNode;
  /** Helper text under the control: format hints, units, context. */
  description?: React.ReactNode;
  /**
   * Validation message. When set, the field renders as invalid and the message
   * replaces the description.
   */
  error?: React.ReactNode;
  /**
   * Trailing content on the label row — a count, a unit, a small action.
   *
   * Exists because seven screens were hand-rolling
   * `<div className="relay-ui-internal flex justify-between items-center mb-2">` around a Label
   * and a hint, each with their own spacing.
   */
  labelAction?: React.ReactNode;
  required?: boolean;
  disabled?: boolean;
  /**
   * `sm` is the dense form shape: a muted 14px label 4px above the control,
   * matching `ReadOnlyField size="sm"` so the two line up in one grid.
   */
  size?: FieldLabelRecipeProps['size'];
  /** Set an explicit id instead of the generated one. */
  htmlFor?: string;
  children: React.ReactNode;
}

/**
 * Label + control + helper text + error, laid out consistently.
 *
 * The control inside does not need any wiring — Field generates the ids and
 * the library's inputs pick them up from context, so the label points at the
 * right control and the error is announced when validation fails.
 *
 *   <Field label="Driver name" error={errors.name?.message} required>
 *     <Input {...register('name')} />
 *   </Field>
 */
export const Field = React.forwardRef<HTMLDivElement, FieldProps>(
  function Field(
    {
      label,
      labelAction,
      description,
      error,
      required = false,
      disabled = false,
      size,
      density,
      htmlFor,
      className,
      children,
      ...props
    },
    ref
  ) {
    const generatedId = React.useId();
    const controlId = htmlFor ?? `${generatedId}-control`;
    const invalid = Boolean(error);

    const context = React.useMemo<FieldContextValue>(
      () => ({
        controlId,
        descriptionId: description ? `${generatedId}-description` : undefined,
        messageId: invalid ? `${generatedId}-message` : undefined,
        invalid,
        disabled,
        required,
      }),
      [controlId, description, generatedId, invalid, disabled, required]
    );

    return (
      <FieldContext.Provider value={context}>
        <div
          ref={ref}
          className={cn(
            // The dense label sits 4px above its control, which is what
            // `compact` already is. Spelling both at every call site is how
            // two fields end up 2px apart from each other.
            fieldRecipe({
              density: density ?? (size === 'sm' ? 'compact' : undefined),
            }),
            className
          )}
          {...props}
        >
          {label || labelAction ? (
            <div className="relay-ui-internal flex min-w-0 items-center justify-between gap-2">
              {label ? (
                <Label
                  htmlFor={controlId}
                  disabled={disabled}
                  size={size}
                  required={required}
                >
                  {label}
                </Label>
              ) : (
                <span />
              )}
              {labelAction ? (
                <span className="shrink-0 text-body-sm text-muted-foreground">
                  {labelAction}
                </span>
              ) : null}
            </div>
          ) : null}

          {children}

          {/* The error takes the description's place rather than stacking, so a
            failing field never grows and pushes the rest of the form down. */}
          {invalid ? (
            <p
              id={context.messageId}
              className={fieldMessageRecipe({ tone: 'error' })}
              // Announced when it appears, without stealing focus.
              role="alert"
            >
              {error}
            </p>
          ) : description ? (
            <p id={context.descriptionId} className={fieldDescriptionRecipe()}>
              {description}
            </p>
          ) : null}
        </div>
      </FieldContext.Provider>
    );
  }
);

/**
 * A standalone label, for the rare control that cannot live inside a Field
 * (a checkbox with inline copy, for instance).
 */
export interface LabelProps
  extends React.ComponentPropsWithoutRef<typeof LabelPrimitive.Root> {
  /**
   * Dim the label to match a disabled control. Field does this for its own
   * label; a standalone Label beside a disabled Checkbox has to be told.
   */
  disabled?: boolean;
  /** `sm` is the dense, muted label. See `fieldLabelRecipe`. */
  size?: FieldLabelRecipeProps['size'];
  /** Draws the asterisk after the text. */
  required?: boolean;
}

export const Label = React.forwardRef<
  React.ElementRef<typeof LabelPrimitive.Root>,
  LabelProps
>(function Label(
  { className, disabled, size, required, children, ...props },
  ref
) {
  return (
    <LabelPrimitive.Root
      ref={ref}
      className={cn(fieldLabelRecipe({ disabled, size }), className)}
      {...props}
    >
      {children}
      {required ? (
        <span className={fieldRequiredMarkerRecipe()} aria-hidden="true">
          *
        </span>
      ) : null}
    </LabelPrimitive.Root>
  );
});

export interface FormLayoutProps extends React.HTMLAttributes<HTMLDivElement> {
  columns?: 1 | 2 | 3 | 4;
  gap?: 'compact' | 'comfortable' | 'relaxed';
}

/**
 * Responsive grid for form fields. Always collapses to one column on mobile —
 * dispatchers use this on phones.
 *
 * A field that should span the full width gets `className="sm:col-span-2"`.
 */
export const FormLayout = React.forwardRef<HTMLDivElement, FormLayoutProps>(
  function FormLayout({ columns, gap, className, ...props }, ref) {
    return (
      <div
        ref={ref}
        className={cn(formLayoutRecipe({ columns, gap }), className)}
        {...props}
      />
    );
  }
);

export interface FormActionsProps extends React.HTMLAttributes<HTMLDivElement> {
  align?: 'start' | 'end' | 'between';
  /** Draw a rule above the buttons. */
  divided?: boolean;
}

/**
 * The button row that closes a form.
 *
 * A right-aligned row from `sm` up; a stacked column below it, reversed, so
 * the primary action is on top and the cancel beneath it. `DialogFooter` and
 * `AlertDialogFooter` share this, so it is the shape of every action row in
 * the product.
 *
 * The order is worth stating because it is a choice rather than a default:
 * primary on top reads as the prominent action, where primary at the bottom
 * would be the one nearest the thumb. This does the former — the docstring
 * used to claim the latter, which was simply wrong.
 */
export const FormActions = React.forwardRef<HTMLDivElement, FormActionsProps>(
  function FormActions({ align, divided, className, ...props }, ref) {
    return (
      <div
        ref={ref}
        className={cn(formActionsRecipe({ align, divided }), className)}
        {...props}
      />
    );
  }
);

export interface ReadOnlyFieldProps
  extends React.HTMLAttributes<HTMLDivElement>,
    ReadOnlyValueRecipeProps {
  label?: React.ReactNode;
  /**
   * The value. Anything empty renders the placeholder instead.
   *
   * `children` works too, and means the same thing — the impound panels were
   * written that way before this moved into the library.
   */
  value?: React.ReactNode;
  /** Helper text under the box. */
  description?: React.ReactNode;
  /** Shown when `value` is null, undefined or an empty string. */
  placeholder?: string;
}

/**
 * A labelled value that cannot be edited, boxed so it lines up with the
 * editable fields beside it.
 *
 *   <ReadOnlyField label="Contact name" value={call.contactName} />
 *
 * The third of three ways to show a value, and the distinction matters:
 *
 *   Field          label + an editable control
 *   FieldValue     label + plain text, for detail panels and mobile cards
 *   ReadOnlyField  label + a control-shaped box, for forms
 *
 * A form that mixes plain text with Inputs in one grid looks broken; this is
 * the shape that stops that. Nine of these were hand-built in the impound
 * modal alone, each as a Label above a `p-3 bg-muted rounded-md` div.
 *
 * `size="sm"` is the dense version for a detail panel: a 32-high bordered box,
 * a muted label 4px above it, and a value clamped to one line. A panel of
 * twenty facts at the default size is mostly white space, which is what it
 * looked like next to the design.
 */
export const ReadOnlyField = React.forwardRef<
  HTMLDivElement,
  ReadOnlyFieldProps
>(function ReadOnlyField(
  {
    label,
    value,
    children,
    description,
    placeholder = 'N/A',
    size,
    className,
    ...props
  },
  ref
) {
  const shown = value ?? children;
  const empty = shown === null || shown === undefined || shown === '';
  const dense = size === 'sm';
  /*
   * Only a plain string gets the clamp. A node passed as the value brings its
   * own layout — the "Where" box is an icon beside a line of text — and
   * wrapping that in a truncating span would collapse the icon.
   */
  const clamp =
    dense && (typeof shown === 'string' || typeof shown === 'number');
  return (
    <div
      ref={ref}
      className={cn(
        uiMarkerInternal,
        dense ? 'space-y-1' : 'space-y-2',
        className
      )}
      {...props}
    >
      {label ? <Label size={dense ? 'sm' : 'md'}>{label}</Label> : null}
      <div className={cn(readOnlyValueRecipe({ size }))}>
        {empty ? (
          <span
            className={
              dense ? 'text-muted-foreground' : 'italic text-muted-foreground'
            }
          >
            {placeholder}
          </span>
        ) : clamp ? (
          <span className="min-w-0 truncate">{shown}</span>
        ) : (
          shown
        )}
      </div>
      {description ? (
        <Text variant="caption" tone="muted">
          {description}
        </Text>
      ) : null}
    </div>
  );
});
